import { useState, useEffect, useCallback } from 'react';
import { ProductSection, GiftShop } from '../types/product';
import { getGiftShopProducts, getGiftShopInfo } from '../api/productService';

interface UseProductsResult {
  productSections: ProductSection[];
  giftShopInfo: GiftShop | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * 기프트샵 상품 데이터를 관리하는 커스텀 훅
 * @param shopId 기프트샵 ID
 * @returns 상품 섹션 목록, 기프트샵 정보, 로딩 상태, 에러 상태, 재요청 함수
 */
export const useProducts = (shopId: string): UseProductsResult => {
  const [productSections, setProductSections] = useState<ProductSection[]>([]);
  const [giftShopInfo, setGiftShopInfo] = useState<GiftShop | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // 기프트샵 정보와 상품 목록을 병렬로 가져오기
      const [shopInfo, products] = await Promise.all([
        getGiftShopInfo(shopId),
        getGiftShopProducts(shopId)
      ]);

      setGiftShopInfo(shopInfo);
      setProductSections(products);
    } catch (err) {
      console.error('상품 데이터를 가져오는데 실패했습니다:', err);
      setError('상품 데이터를 불러올 수 없습니다.');
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  const refetch = useCallback(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (shopId) {
      fetchData();
    }
  }, [shopId, fetchData]);

  return {
    productSections,
    giftShopInfo,
    loading,
    error,
    refetch
  };
}; 